// The React-owned module sub-controls bar (Phase 4).
//
// Renders `.module-sub-controls` for a converted module: one `<BentoSelect>` per
// descriptor in CONTROLS_CONFIG, emitting the same DOM the legacy `controlsHTML`
// produced (same `.module-controls {toggle}` classes, options, order) so the
// shared CSS styles it identically.
//
// State flows through the store: the current values are read from the module's
// entry (by id), and a change dispatches `setModuleControls`. The two legacy
// side effects around a control change — snapshot for undo, and re-render the
// body — are injected as `saveState` / `refreshBody` callbacks by the bridge
// (they live in the legacy `main.js` until the shell itself is React), keeping
// this component free of any `window.*` coupling.
//
// This replaces the per-toggle `change` listeners in `attachModuleEvents` for
// converted modules.

import { useState } from "react";
import { clubFromName } from "@bento/shared";
import { useBentoStore, selectModuleFixture } from "../store/index.js";
import type { ModuleControls as ModuleControlsState } from "../store/module.js";
import BentoSelect, { type BentoSelectOption } from "./BentoSelect.js";
import { CONTROLS_CONFIG, leagueSeasonOptions, type ControlDescriptor } from "./moduleControlsConfig.js";
import { playerOptionsFor } from "../data/playerControlOptions.js";
import { fixtureTeams } from "../lib/fixtureTeams.js";

/** Abbreviate a team name like the legacy `abbrevTeamSelect` (3-letter short). */
function abbr(name: string): string {
  return clubFromName(name)?.short ?? name.slice(0, 3).toUpperCase();
}

/** The fixture's two teams as options, labels abbreviated (value stays the full name). */
function teamOptions(fixture: string): BentoSelectOption[] {
  return fixtureTeams(fixture).map((name) => ({ value: name, label: abbr(name) }));
}

/** The market scope select: Match + the fixture's two teams. The option VALUES
 *  are the fixed dummy-data slot keys "Match"/"ARS"/"EVE" (verbatim from legacy
 *  — the market data is keyed by these home/away slots, not real club codes);
 *  only the LABELS are the fixture teams. */
function scopeOptions(fixture: string): BentoSelectOption[] {
  const [home = "", away = ""] = fixture.split(" v ");
  return [
    { value: "Match", label: "Match" },
    { value: "ARS", label: abbr(home) },
    { value: "EVE", label: abbr(away) },
  ];
}

export interface ModuleControlsProps {
  /** The module's store id (`mod.dataset.moduleId`). */
  moduleId: string;
  /** Module title — selects the control set from CONTROLS_CONFIG. */
  title: string;
  /** Snapshot current state for undo, before applying the change (legacy `saveState`). */
  saveState: () => void;
  /** Re-render the module body after the store change (legacy `refreshModuleBody`). */
  refreshBody: () => void;
  /** Cross-module side effect after a control change (legacy `main.js`). The
   *  store change + body refresh have already run; this fans the value out to
   *  other modules — e.g. the referee "link" sync propagates `ref` across all
   *  linked referee modules. Kept in `main.js` (the Phase-3/4 boundary), so this
   *  component stays free of any `window.*` / cross-module coupling. */
  onControlChange?: (field: keyof ModuleControlsState, value: string) => void;
}

/** A link/unlink toggle button (`.ref-link-btn` / `.bk-link-btn`). Its
 *  linked/unlinked state is *local* — ephemeral UI, not store state (the legacy
 *  button was never captured by `saveState`/undo). It renders the `.unlinked`
 *  class as a real DOM class so `main.js`'s cross-module sync can read it. */
function LinkButton({ d }: { d: ControlDescriptor }) {
  const [unlinked, setUnlinked] = useState(false);
  return (
    <button
      className={unlinked ? `${d.className} unlinked` : d.className}
      title={unlinked ? d.titleUnlinked ?? d.title : d.title}
      onClick={() => setUnlinked((v) => !v)}
      dangerouslySetInnerHTML={{ __html: d.iconsHtml ?? "" }}
    />
  );
}

export default function ModuleControls({
  moduleId,
  title,
  saveState,
  refreshBody,
  onControlChange,
}: ModuleControlsProps) {
  // Subscribe to this module's control values only (not the whole module — that
  // changes on drag/resize and would re-render controls needlessly).
  const controls = useBentoStore(
    (s) => s.modules.find((m) => m.id === moduleId)?.controls
  );
  // The team toggle's options are this module's fixture's teams — the live match,
  // or the frozen one while pinned (the same match the widget body shows).
  const fixture = useBentoStore((s) => selectModuleFixture(s, moduleId));
  // …and the League Table's season options are the current league's seasons.
  const competitions = useBentoStore((s) => s.competitions);
  const competitionId = useBentoStore((s) => s.competitionId);
  const descriptors = CONTROLS_CONFIG[title];
  if (!descriptors) return null;

  function onChange(field: keyof ModuleControlsState, value: string) {
    // Order mirrors the legacy toggle listener: snapshot first (so undo reverts
    // this change), then update the store, then re-render the body from it, and
    // finally run any cross-module side effect (e.g. the referee link sync) —
    // which reads the just-written store value.
    saveState();
    useBentoStore.getState().setModuleControls(moduleId, { [field]: value });
    refreshBody();
    onControlChange?.(field, value);
  }

  // Render a Fragment, NOT a wrapping `.module-sub-controls` div: the legacy
  // shell (createModule) already emits that div and is the React root container,
  // so wrapping again would nest two `.module-sub-controls` (double padding /
  // background / border). The controls are the div's direct children, matching
  // the legacy DOM exactly.
  return (
    <>
      {descriptors.map((d) => {
        // Link/unlink toggle button — local UI state, no store field. Rendered
        // as a real DOM button so `main.js`'s cross-module sync reads `.unlinked`.
        if (d.kind === "linkButton") {
          return <LinkButton key={d.className} d={d} />;
        }
        // Two-state toggle BUTTON (not a select): flips `viewMode` between its
        // two values; the `.active` class (alternate value) drives which icon the
        // shared CSS shows. Emits the same `<button class title>` DOM as legacy.
        if (d.kind === "viewButton") {
          const [base, alt] = d.values ?? ["", ""];
          const current = controls?.viewMode ?? base;
          const active = current === alt;
          return (
            <button
              key={d.field}
              className={active ? `${d.className} active` : d.className}
              title={d.title}
              onClick={() => onChange("viewMode", current === base ? alt : base)}
              dangerouslySetInnerHTML={{ __html: d.iconsHtml ?? "" }}
            />
          );
        }
        // Every remaining descriptor (team / scope / player / static select)
        // reads/writes a store field.
        const field = d.field;
        if (!field) return null;
        // Options: team/scope are fixture-derived; player is derived from this
        // module's own scope + market type (so a scope/type change re-narrows the
        // list); everything else has static options.
        const options =
          d.kind === "team"
            ? teamOptions(fixture)
            : d.kind === "scope"
              ? scopeOptions(fixture)
              : d.kind === "player"
                ? playerOptionsFor(
                    d.marketKind ?? "goals",
                    fixture,
                    controls?.scope,
                    controls?.marketType
                  ).map((p) => ({ value: p, label: p }))
                : d.kind === "leagueSeason"
                  ? leagueSeasonOptions(competitions, competitionId)
                  : d.options ?? [];
        // Clamp to a valid option: the stored value if it's still selectable
        // (a team survives a fixture change only if it's in the new fixture),
        // else the descriptor default / first option (the home side for teams).
        const stored = controls?.[field];
        const value =
          stored != null && options.some((o) => o.value === stored)
            ? stored
            : d.defaultValue ?? options[0]?.value ?? "";
        return (
          <BentoSelect
            key={field}
            className={d.className}
            ariaLabel={d.ariaLabel}
            value={value}
            options={options}
            onChange={(v) => onChange(field, v)}
          />
        );
      })}
    </>
  );
}
